import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Search, Filter, RefreshCw, Loader2, CircleCheck, ShieldOff,
  CircleHelp, CircleAlert, ChevronDown, ChevronRight, Info, Copy,
  Globe,
} from "lucide-react";
import { AdminSms } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LanguageSelector from "@/components/LanguageSelector";
import { useI18n } from "@/lib/i18n";

/**
 * Admin SMS Log — Phase B minimal.
 *
 * Backend-first correctness. UI shows the four core columns per spec:
 *   date/time, masked recipient, category, status
 * Each row is expandable to reveal the safe details (correlation ID,
 * reference ID, provider code, retry eligibility, batch id, etc.).
 * The 14-column desktop table is intentionally deferred to a follow-up.
 *
 * Never shows: full mobile number, OTP body, provider payload, Authkey.
 */

const STATUS_META = {
  queued:               { icon: CircleHelp,  label: "Queued",              tone: "text-white/70 bg-white/5" },
  processing:           { icon: Loader2,     label: "Processing",           tone: "text-sky-200 bg-sky-500/10" },
  submitted:            { icon: CircleCheck, label: "Submitted",            tone: "text-sky-200 bg-sky-500/10" },
  delivered:            { icon: CircleCheck, label: "Delivered",            tone: "text-emerald-200 bg-emerald-500/10" },
  simulated:            { icon: CircleCheck, label: "Simulated",            tone: "text-white/60 bg-white/5" },
  already_sent:         { icon: CircleCheck, label: "Duplicate skipped",    tone: "text-white/60 bg-white/5" },
  failed:               { icon: CircleAlert, label: "Failed",               tone: "text-rose-200 bg-rose-500/10" },
  provider_rejected:    { icon: ShieldOff,   label: "Provider rejected",    tone: "text-rose-200 bg-rose-500/10" },
  ip_blocked:           { icon: ShieldOff,   label: "IP blocked",           tone: "text-amber-200 bg-amber-500/10" },
  expired:              { icon: CircleAlert, label: "Expired",              tone: "text-white/60 bg-white/5" },
  retry_pending:        { icon: Loader2,     label: "Retry pending",        tone: "text-sky-200 bg-sky-500/10" },
  retry_succeeded:      { icon: CircleCheck, label: "Retry succeeded",      tone: "text-emerald-200 bg-emerald-500/10" },
  retry_failed:         { icon: CircleAlert, label: "Retry failed",         tone: "text-rose-200 bg-rose-500/10" },
  cancelled:            { icon: CircleAlert, label: "Cancelled",            tone: "text-white/60 bg-white/5" },
  requires_new_otp:     { icon: Info,        label: "Requires fresh OTP",   tone: "text-[#F0D788] bg-[#D4AF37]/10" },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { icon: CircleHelp, label: status || "unknown", tone: "text-white/60 bg-white/5" };
  const Icon = meta.icon;
  return (
    <span
      data-testid={`sms-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] uppercase tracking-wider ${meta.tone}`}
    >
      <Icon size={11} className={status === "processing" || status === "retry_pending" ? "animate-spin" : ""} aria-hidden="true" />
      <span>{meta.label}</span>
    </span>
  );
}

function HealthChip({ h }) {
  if (!h) return null;
  const ICON = { ok: CircleCheck, ip_blocked: ShieldOff, egress_not_whitelisted: ShieldOff, degraded: CircleAlert, unavailable: CircleAlert, not_checked: CircleHelp, configuration_required: Info }[h.status] || CircleHelp;
  const tone = { ok: "text-emerald-200 bg-emerald-500/10 border-emerald-500/30", ip_blocked: "text-amber-200 bg-amber-500/10 border-amber-500/30", egress_not_whitelisted: "text-amber-200 bg-amber-500/10 border-amber-500/30", degraded: "text-rose-200 bg-rose-500/10 border-rose-500/30", unavailable: "text-rose-200 bg-rose-500/10 border-rose-500/30", not_checked: "text-white/70 bg-white/5 border-white/15", configuration_required: "text-sky-200 bg-sky-500/10 border-sky-500/30" }[h.status] || "text-white/70 bg-white/5 border-white/15";
  return (
    <div data-testid="sms-provider-health" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${tone} min-w-0`}>
      <ICON size={13} aria-hidden="true" />
      <span className="font-semibold whitespace-nowrap truncate">{h.text}</span>
      <span className="opacity-60 text-[10px] hidden sm:inline truncate">
        · {h.environment}
        {h.egress_fingerprint ? ` · fp:${h.egress_fingerprint}` : ""}
        {h.egress?.state === "whitelist_not_configured" ? " · whitelist not configured" : h.egress?.state === "ok" ? " · egress whitelisted" : ""}
      </span>
    </div>
  );
}

function EgressCheckButton() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try { setRes(await AdminSms.egressCheck()); }
    catch (e) { toast.error(e?.response?.data?.detail?.message_en || "Egress check failed"); }
    finally { setBusy(false); }
  };
  const label = { static: "STATIC", multiple_or_rotating: "MULTIPLE / ROTATING", insufficient_samples: "NEED MORE SAMPLES" };
  return (
    <div className="inline-flex flex-wrap items-center gap-2">
      <button data-testid="sms-egress-check" onClick={run} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-[#D4AF37]/40 text-[#F0D788] hover:bg-[#D4AF37]/10 disabled:opacity-40">
        <Globe size={13} aria-hidden="true" />{busy ? "Checking…" : "Check egress IP"}
      </button>
      {res && (
        <span data-testid="sms-egress-result" className="text-[11px] text-white/75 font-mono">
          {res.environment} · {res.egress_ip || "unresolved"}{res.sources_agree ? "" : " (sources disagree)"} · seen 30d: {res.distinct_ips_seen_30d.join(", ") || "—"} ({res.samples_30d}) · {label[res.assessment]}
          {" · "}
          <span data-testid="sms-egress-whitelist" className={res.whitelist?.configured ? (res.whitelist.live_ip_whitelisted ? "text-emerald-300" : "text-amber-300 font-semibold") : "text-white/50"}>
            {!res.whitelist?.configured ? "whitelist not configured" : res.whitelist.live_ip_whitelisted ? "IN MSG91 WHITELIST" : "NOT IN MSG91 WHITELIST"}
          </span>
        </span>
      )}
    </div>
  );
}

export default function AdminSmsLog() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("");
  const [environment, setEnvironment] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [health, setHealth] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showRetryModal, setShowRetryModal] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (environment) params.environment = environment;
      if (errorCode) params.error_code = errorCode;
      const r = await AdminSms.log(params);
      setItems(r.items || []); setTotal(r.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load SMS log");
    } finally { setBusy(false); }
  }, [page, pageSize, status, environment, errorCode]);

  const loadHealth = useCallback(async () => {
    try { setHealth(await AdminSms.providerHealth()); }
    catch { setHealth({ status: "not_checked", text: "SMS provider: Not checked" }); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  return (
    <div data-testid="admin-sms-log-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="border-b border-white/10 backdrop-blur bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/admin/dashboard" className="flex items-center gap-2 min-w-0">
            <Logo size={32} />
            <span className="font-display text-sm sm:text-base truncate">SMS Log</span>
          </Link>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <HealthChip h={health} />
            <EgressCheckButton />
            <LanguageSelector testId="admin-sms-log-lang" />
            <button
              onClick={() => { load(); loadHealth(); }}
              disabled={busy}
              data-testid="admin-sms-log-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-[#D4AF37]/60 text-xs disabled:opacity-40"
            >
              <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select
            value={status}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            data-testid="filter-status"
            className="text-xs px-3 py-1.5 rounded-full border border-white/15 bg-[#04102B] focus:border-[#D4AF37]/60 outline-none min-w-0 max-w-full"
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_META).map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <select
            value={errorCode}
            onChange={(e) => { setPage(1); setErrorCode(e.target.value); }}
            data-testid="filter-error-code"
            className="text-xs px-3 py-1.5 rounded-full border border-white/15 bg-[#04102B] focus:border-[#D4AF37]/60 outline-none"
          >
            <option value="">All error codes</option>
            <option value="418">418 · IP not whitelisted</option>
            <option value="205">205 · Invalid template</option>
            <option value="None">— no error —</option>
          </select>
          <select
            value={environment}
            onChange={(e) => { setPage(1); setEnvironment(e.target.value); }}
            data-testid="filter-env"
            className="text-xs px-3 py-1.5 rounded-full border border-white/15 bg-[#04102B] focus:border-[#D4AF37]/60 outline-none"
          >
            <option value="">All environments</option>
            <option value="preview">Preview</option>
            <option value="production">Production</option>
          </select>
          {(health?.status === "ip_blocked") && (
            <button
              onClick={() => setShowRetryModal(true)}
              data-testid="open-bulk-retry-drawer"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-400/40 hover:border-amber-400 bg-amber-500/10 text-amber-200 text-xs"
              disabled
              title="Bulk retry is unlocked only after provider health returns to OK"
            >
              Bulk retry 418 (disabled — provider IP blocked)
            </button>
          )}
          {health?.status === "ok" && (
            <button
              onClick={() => setShowRetryModal(true)}
              data-testid="open-bulk-retry-drawer"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/40 hover:border-emerald-400 bg-emerald-500/10 text-emerald-200 text-xs"
            >
              Retry eligible 418-rejected SMS
            </button>
          )}
        </div>

        {/* Table (desktop) / Cards (mobile) */}
        <div className="bg-white/[0.03] rounded-2xl border border-white/8 overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1.4fr_1.2fr_1fr_1fr_auto] gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider text-white/50 border-b border-white/10">
            <div>Date &amp; time</div><div>Recipient</div><div>Category</div><div>Status</div><div>Details</div>
          </div>
          {items.length === 0 && !busy && (
            <div data-testid="sms-log-empty" className="p-8 text-center text-white/50 text-sm">No SMS matches these filters.</div>
          )}
          {busy && <div className="p-8 text-center text-white/50 text-sm"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>}
          {err && <div data-testid="sms-log-error" className="p-4 text-rose-300 text-sm bg-rose-500/10 border-b border-rose-500/20">{typeof err === "string" ? err : JSON.stringify(err)}</div>}
          {items.map((r) => {
            const isOpen = !!expanded[r.id];
            return (
              <div key={r.id} className="border-b border-white/5 last:border-b-0" data-testid={`sms-row-${r.id}`}>
                <button
                  onClick={() => toggle(r.id)}
                  className="w-full text-left grid sm:grid-cols-[1.4fr_1.2fr_1fr_1fr_auto] gap-2 sm:gap-3 px-4 py-3 hover:bg-white/[0.03] transition min-w-0"
                >
                  <div className="text-xs text-white/80 whitespace-nowrap">{r.created_at?.slice(0, 19).replace("T", " ")}</div>
                  <div data-testid={`sms-recipient-${r.id}`} className="text-xs text-white/90 font-mono truncate">{r.recipient_masked}</div>
                  <div className="text-xs text-white/70 truncate">{r.category_label}</div>
                  <div className="text-xs"><StatusChip status={r.status} /></div>
                  <div className="text-xs text-white/50 flex items-center justify-end">
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </div>
                </button>
                {isOpen && (
                  <div data-testid={`sms-details-${r.id}`} className="px-4 pb-4 pt-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-[11px] text-white/70 [overflow-wrap:anywhere]">
                    <div><span className="opacity-50">ID:</span> {r.id}</div>
                    <div><span className="opacity-50">Correlation:</span> {r.correlation_id || "—"}</div>
                    <div><span className="opacity-50">Ref:</span> {r.reference_id || "—"}</div>
                    <div><span className="opacity-50">Provider:</span> {r.provider}</div>
                    <div><span className="opacity-50">Env:</span> {r.environment}</div>
                    <div><span className="opacity-50">Error code:</span> {r.error_code || "—"}</div>
                    <div><span className="opacity-50">Reason code:</span> {r.safe_reason_code || "—"}</div>
                    <div className="sm:col-span-2 lg:col-span-3"><span className="opacity-50">Reason:</span> {r.error_description || "—"}</div>
                    <div><span className="opacity-50">Attempts:</span> {r.attempts}</div>
                    <div><span className="opacity-50">Retryable:</span> {String(r.retryable ?? "—")}</div>
                    <div><span className="opacity-50">Batch:</span> {r.batch_id || "—"}</div>
                    {r.requires_new_otp && (
                      <div className="sm:col-span-2 lg:col-span-3 text-amber-200 bg-amber-500/10 rounded px-2 py-1">
                        This OTP failed. The user must request a new OTP through the normal flow — the previous code has been retired and will not be resent.
                      </div>
                    )}
                    {r.message_body && (
                      <div className="sm:col-span-2 lg:col-span-3 mt-1 opacity-70">
                        <span className="opacity-50">Body:</span> {r.message_body}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-white/60">
          <span>Page {page} of {Math.max(1, Math.ceil(total / pageSize))} · {total} rows</span>
          <select
            value={pageSize}
            onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
            data-testid="page-size"
            className="ml-auto px-2 py-1 rounded border border-white/15 bg-[#04102B] text-white/80"
          >
            {[25, 50, 100].map(n => <option key={n} value={n}>{`${n} / page`}</option>)}
          </select>
          <button
            disabled={page === 1 || busy}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            data-testid="page-prev"
            className="px-3 py-1 rounded border border-white/15 disabled:opacity-40"
          >Prev</button>
          <button
            disabled={page >= Math.ceil(total / pageSize) || busy}
            onClick={() => setPage(p => p + 1)}
            data-testid="page-next"
            className="px-3 py-1 rounded border border-white/15 disabled:opacity-40"
          >Next</button>
        </div>
      </div>

      {showRetryModal && (
        <BulkRetryDrawer onClose={() => { setShowRetryModal(false); load(); loadHealth(); }} />
      )}
    </div>
  );
}

function BulkRetryDrawer({ onClose }) {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { setPreview(await AdminSms.bulkRetryPreview()); }
      catch (e) { setErr(e?.response?.data?.detail || "preview failed"); }
    })();
  }, []);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      setResult(await AdminSms.bulkRetryConfirm(reason, confirmation));
    } catch (e) {
      setErr(e?.response?.data?.detail || "retry failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="bulk-retry-drawer" className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0A1F44] border border-[#D4AF37]/30 rounded-2xl max-w-lg w-full p-6 shadow-2xl [overflow-wrap:anywhere]">
        <h2 className="font-display text-lg mb-1">Retry eligible 418-rejected SMS</h2>
        <p className="text-xs text-white/60 mb-4">Only non-OTP messages are retried. Old OTPs are retired — users must request a fresh code.</p>
        {preview && (
          <div className="text-[11px] space-y-1 bg-white/[0.03] rounded-lg p-3 mb-4">
            <div>Eligible non-OTP: <strong className="text-emerald-200">{preview.eligible_non_otp}</strong></div>
            <div>Excluded OTP (flip to `requires_new_otp`): {preview.excluded_otp_will_require_new}</div>
            <div>Already delivered: {preview.already_delivered}</div>
            <div>Cancelled / obsolete: {preview.cancelled_or_obsolete}</div>
            <div className="opacity-60 mt-2">Rate: {preview.rate_limit} · Batch: {preview.batch_size} · Env: {preview.environment}</div>
            <div className="opacity-60">Provider health OK: {String(preview.provider_health_ok)}</div>
          </div>
        )}
        <label className="block text-[10px] uppercase tracking-wider text-white/55 mt-2 mb-1">Reason (required, ≥ 8 chars, audited)</label>
        <input
          value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid="bulk-retry-reason"
          className="w-full text-sm bg-[#04102B] border border-white/15 rounded px-3 py-2 focus:border-[#D4AF37]/60 outline-none"
        />
        <label className="block text-[10px] uppercase tracking-wider text-white/55 mt-3 mb-1">Type exactly: <code className="text-[#F0D788]">RETRY ELIGIBLE SMS</code></label>
        <input
          value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
          data-testid="bulk-retry-confirmation"
          className="w-full text-sm bg-[#04102B] border border-white/15 rounded px-3 py-2 focus:border-[#D4AF37]/60 outline-none font-mono"
        />
        {err && <div data-testid="bulk-retry-error" className="mt-3 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded px-3 py-2">{typeof err === "string" ? err : JSON.stringify(err)}</div>}
        {result && <div data-testid="bulk-retry-result" className="mt-3 text-[11px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/25 rounded px-3 py-2">
          Batch {result.batch_id?.slice(0, 8)} · Retried {result.retried} · OTPs flipped {result.otp_flipped}{result.paused_on_418 ? " · Paused on repeat 418" : ""}
        </div>}
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 rounded-full border border-white/15 text-xs">Close</button>
          <button
            onClick={submit}
            disabled={busy || reason.length < 8 || confirmation !== "RETRY ELIGIBLE SMS"}
            data-testid="bulk-retry-submit"
            className="ml-auto px-4 py-1.5 rounded-full btn-gold text-xs disabled:opacity-40"
          >
            {busy ? "Retrying…" : "Retry now"}
          </button>
        </div>
      </div>
    </div>
  );
}
